import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DateRangePicker, { DateRange, defaultRange } from './DateRangePicker';
import TimeSeriesChart from './TimeSeriesChart';
import SortableTable, { Column } from './SortableTable';
import VolumeCards, {
  RANGE_LABELS,
  fmtExact,
  fmtRate,
  deliveryRateColor,
  acceptRateColor,
} from './VolumeCards';

/**
 * /reports — landing page for the Reporting tab.
 *
 * Layout (top to bottom):
 *   - Header: title + freshness badge
 *   - Date range picker (unchanged presets, timezone and Refresh) plus the
 *     Source and Client filters
 *   - Four volume cards — Today / <range> / Gross / Delivery rate
 *   - Volume over time
 *   - ONE breakdown at a time, chosen by tab
 *
 * Why tabs (2026-09-14)
 * ---------------------
 * The page used to stack five breakdown tables. People read one at a time
 * and scrolled past the rest, and each new dimension (source, then client)
 * made the scroll worse. Tabs keep every view reachable in one click
 * without making the default view pay for the ones nobody opened.
 *
 * The active tab is persisted to localStorage — same pattern as the
 * dashboard's view mode — so someone who lives in By leg does not land on
 * By data body every morning.
 *
 * Tab data is fetched LAZILY. Only the overview (cards + chart) and the
 * active tab's rows load on mount; switching tabs fetches that tab once and
 * keeps it. Changing the range or a filter invalidates everything, because
 * every panel is scoped by them.
 *
 * Vocabulary, and why two rates are not one rate
 * ----------------------------------------------
 * By data body shows DELIVERY RATE: leg-node accepts over brain accepts.
 * It is fan-out aware and routinely exceeds 100%, because one accepted
 * lead sent to three destinations is three deliveries. By leg shows ACCEPT
 * RATE: that destination's own accepts over what reached it, a true 0..1
 * percentage. They answer different questions and must not share a name.
 *
 * Account isolation is enforced server-side; every endpoint resolves the
 * account from the JWT. There is no caller-supplied tenant id.
 */

type TabKey = 'bodies' | 'legs' | 'types' | 'sources' | 'clients';

const TAB_KEY = 'databodies.reporting.tab';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'bodies',  label: 'By data body' },
  { key: 'legs',    label: 'By leg' },
  { key: 'types',   label: 'By intake type' },
  { key: 'sources', label: 'By source' },
  { key: 'clients', label: 'By client' },
];

function storedTab(): TabKey {
  const raw = localStorage.getItem(TAB_KEY);
  return TABS.some(t => t.key === raw) ? (raw as TabKey) : 'bodies';
}

function FreshnessBadge({ freshness }: { freshness: api.ReportingFreshness }) {
  const config = {
    fresh:  { label: 'Live',       color: '#047857', bg: '#ecfdf5' },
    stale:  { label: 'Refreshing', color: '#92400e', bg: '#fffbeb' },
    cold:   { label: 'Reloaded',   color: '#1d4ed8', bg: '#eff6ff' },
    bypass: { label: 'Forced',     color: '#374151', bg: '#f3f4f6' },
  }[freshness];
  return (
    <span style={{
      fontSize: 11,
      fontWeight: 600,
      padding: '2px 8px',
      color: config.color,
      background: config.bg,
      borderRadius: 999,
    }}>
      {config.label}
    </span>
  );
}

export default function ReportingPage() {
  const { organization } = useAuth();
  // Use the account's configured timezone for date math. Falls back to the
  // browser TZ if the org has none — same fallback the dashboard's
  // "today" tally uses (see backend `todayStartUtc`).
  const tz = organization?.timezone || undefined;
  const [range, setRange] = useState<DateRange>(() => defaultRange(tz));
  const [tab, setTab] = useState<TabKey>(storedTab);

  // '' = all sources. Otherwise a Source id, or 'unassigned' for bodies with
  // no source. Scopes every inbound panel: the cards, the chart, the type
  // split, the body table — and the leg table, where it reads as "where does
  // this partner's traffic end up".
  const [sourceFilter, setSourceFilter] = useState<string>('');
  // Who we DELIVER to. Deliberately narrower than the source filter: it
  // scopes the By leg tab ONLY. The cards, the chart and every inbound
  // breakdown are brain-level measurements where a client has no meaning,
  // and quietly reinterpreting them would be the kind of number that is
  // wrong without looking wrong. Drill into a client for their full view.
  const [clientFilter, setClientFilter] = useState<string>('');

  const [overview, setOverview] = useState<api.ReportingOverviewResponse | null>(null);
  const [bodyRows, setBodyRows] = useState<api.ReportingBodyVolumeRow[] | null>(null);
  const [legRows, setLegRows] = useState<api.ReportingLegVolumeRow[] | null>(null);
  const [sourceRows, setSourceRows] = useState<api.ReportingSourceCompanyRow[] | null>(null);
  const [clientRows, setClientRows] = useState<api.ReportingClientRow[] | null>(null);

  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { from, to } = range;
  const src = sourceFilter || undefined;
  const cli = clientFilter || undefined;

  // The filter dropdowns are built from the source/client rollup rows rather
  // than /api/sources and /api/clients, so the lists are exactly "those with
  // traffic in this range" plus the unassigned bucket. Offering a partner
  // that has nothing to show would be a dead end, so both lists load up
  // front regardless of which tab is open — they are small.
  const loadFilters = useCallback(async (forceFresh: boolean) => {
    const [s, c] = await Promise.all([
      api.getReportingSources(from, to, forceFresh),
      api.getReportingClients(from, to, forceFresh),
    ]);
    setSourceRows(s.sources);
    setClientRows(c.clients);
  }, [from, to]);

  const loadOverview = useCallback(async (forceFresh: boolean) => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        api.getReportingOverview(from, to, forceFresh, src).then(setOverview),
        loadFilters(forceFresh),
      ]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [from, to, src, loadFilters]);

  // Any change to the range or the source filter invalidates every cached
  // tab, not just the visible one — otherwise switching tabs would show the
  // previous range's numbers under the new range's header.
  useEffect(() => {
    setBodyRows(null);
    setLegRows(null);
    void loadOverview(false);
  }, [loadOverview]);

  // The client filter scopes only the leg table, so it invalidates only that.
  useEffect(() => {
    setLegRows(null);
  }, [cli]);

  useEffect(() => {
    let cancelled = false;
    async function loadTab() {
      if (tab === 'bodies' && bodyRows === null) {
        setTabLoading(true);
        try {
          const r = await api.getReportingBodyVolume(from, to, false, src);
          if (!cancelled) {setBodyRows(r.bodies);}
        } catch (err: any) {
          if (!cancelled) {setError(err?.response?.data?.error || err?.message || 'Failed to load data bodies');}
        } finally {
          if (!cancelled) {setTabLoading(false);}
        }
      } else if (tab === 'legs' && legRows === null) {
        setTabLoading(true);
        try {
          const r = await api.getReportingLegs(from, to, false, src, cli);
          if (!cancelled) {setLegRows(r.legs);}
        } catch (err: any) {
          if (!cancelled) {setError(err?.response?.data?.error || err?.message || 'Failed to load legs');}
        } finally {
          if (!cancelled) {setTabLoading(false);}
        }
      }
    }
    void loadTab();
    return () => { cancelled = true; };
  }, [tab, bodyRows, legRows, from, to, src, cli]);

  const selectTab = (key: TabKey) => {
    localStorage.setItem(TAB_KEY, key);
    setTab(key);
  };

  const refresh = () => {
    setBodyRows(null);
    setLegRows(null);
    void loadOverview(true);
  };

  const rangeLabel = RANGE_LABELS[range.preset];

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1280, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#111827' }}>Reporting</h1>
        {overview && <FreshnessBadge freshness={overview.freshness} />}
      </div>
      <p style={{ marginTop: 4, marginBottom: 20, color: '#6b7280', fontSize: 14 }}>
        Every accept and reject from your brain, vertebrae, and legs. Account-isolated; no other org sees these numbers.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <DateRangePicker
          value={range}
          onChange={setRange}
          onRefresh={refresh}
          timezone={tz}
        />
        <SourceFilter
          value={sourceFilter}
          onChange={setSourceFilter}
          rows={sourceRows ?? []}
        />
        <ClientFilter
          value={clientFilter}
          onChange={setClientFilter}
          rows={clientRows ?? []}
          onPick={() => selectTab('legs')}
        />
      </div>

      {error && (
        <div style={{
          padding: 16,
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
          color: '#991b1b',
          marginBottom: 20,
        }}>
          {error}
        </div>
      )}

      {loading && !overview && (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
      )}

      {overview && (
        <>
          <div style={{ marginBottom: 24 }}>
            <VolumeCards volume={overview.volume} preset={range.preset} />
          </div>

          <SectionTitle>Volume over time</SectionTitle>
          <div style={{ marginBottom: 28 }}>
            <TimeSeriesChart data={overview.series} tz={tz} />
          </div>

          <Tabs active={tab} onSelect={selectTab} />

          <div style={{ marginTop: 16 }}>
            {tabLoading && (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
            )}

            {!tabLoading && tab === 'bodies' && bodyRows && (
              <BodiesTable rows={bodyRows} rangeLabel={rangeLabel} />
            )}
            {!tabLoading && tab === 'legs' && legRows && (
              <LegsTable rows={legRows} rangeLabel={rangeLabel} />
            )}
            {tab === 'types' && <TypeTable types={overview.sources} />}
            {tab === 'sources' && <SourceCompanyTable rows={sourceRows ?? []} />}
            {tab === 'clients' && <ClientTable rows={clientRows ?? []} />}
          </div>

          <TabFootnote tab={tab} />
        </>
      )}
    </div>
  );
}

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <h2 style={{
      margin: '0 0 12px 0',
      fontSize: 16,
      fontWeight: 600,
      color: '#374151',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      ...style,
    }}>
      {children}
    </h2>
  );
}

function Tabs({ active, onSelect }: { active: TabKey; onSelect: (k: TabKey) => void }) {
  return (
    <div style={{
      display: 'flex',
      gap: 4,
      borderBottom: '1px solid #e5e7eb',
      flexWrap: 'wrap',
    }}>
      {TABS.map(t => {
        const on = t.key === active;
        return (
          <button
            key={t.key}
            onClick={() => onSelect(t.key)}
            style={{
              padding: '10px 16px',
              fontSize: 14,
              fontWeight: on ? 600 : 500,
              color: on ? '#2563eb' : '#6b7280',
              background: 'transparent',
              border: 'none',
              borderBottom: '2px solid ' + (on ? '#2563eb' : 'transparent'),
              marginBottom: -1,
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * One line under the active table explaining what its rate means. The two
 * rates on this page are different measurements with similar names, and a
 * footnote at the point of confusion beats a tooltip nobody hovers.
 */
function TabFootnote({ tab }: { tab: TabKey }) {
  const text: Partial<Record<TabKey, string>> = {
    bodies:
      'Delivery rate is fan-out aware — leads delivered divided by leads accepted. ' +
      'One lead sent to three destinations counts three times, so over 100% is normal.',
    legs:
      'Accept rate is this destination\'s own: what it took divided by what reached it. ' +
      'It is a true percentage and never exceeds 100%.',
    types:
      'How leads arrived, not who sent them. "By source" is the partner view.',
  };
  const t = text[tab];
  if (!t) {return null;}
  return (
    <p style={{ marginTop: 12, fontSize: 13, color: '#9ca3af' }}>{t}</p>
  );
}

// ---------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------

function SourceFilter({
  value,
  onChange,
  rows,
}: {
  value: string;
  onChange: (v: string) => void;
  rows: api.ReportingSourceCompanyRow[];
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6b7280' }}>
      <span style={{ fontWeight: 600 }}>Source</span>
      <select
        className="form-select"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ minWidth: 180, height: 38 }}
      >
        <option value="">All sources</option>
        {rows.map(r => (
          <option key={r.sourceId} value={r.sourceId}>
            {r.sourceId === api.UNASSIGNED_SOURCE ? 'Unassigned' : r.sourceName || '(unnamed source)'}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Client picker. Scopes the By leg tab only — see the note on `clientFilter`
 * above. Picking one also switches to that tab, because a filter that
 * changes nothing on the screen you are looking at reads as broken.
 */
function ClientFilter({
  value,
  onChange,
  rows,
  onPick,
}: {
  value: string;
  onChange: (v: string) => void;
  rows: api.ReportingClientRow[];
  onPick: () => void;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6b7280' }}>
      <span style={{ fontWeight: 600 }}>Client</span>
      <select
        className="form-select"
        value={value}
        onChange={e => {
          onChange(e.target.value);
          if (e.target.value) {onPick();}
        }}
        style={{ minWidth: 180, height: 38 }}
        title="Narrows the By leg tab to one buyer's destinations"
      >
        <option value="">All clients</option>
        {rows.map(r => (
          <option key={r.clientId} value={r.clientId}>
            {r.clientId === api.UNASSIGNED_CLIENT ? 'Unassigned' : r.clientName || '(unnamed client)'}
          </option>
        ))}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------------
// Tab 1 — By data body
// ---------------------------------------------------------------------

function BodiesTable({
  rows,
  rangeLabel,
}: {
  rows: api.ReportingBodyVolumeRow[];
  rangeLabel: string;
}) {
  const columns: Column<api.ReportingBodyVolumeRow>[] = [
    {
      key: 'source',
      label: 'Source',
      width: '160px',
      sortValue: r => r.sourceName ?? '',
      render: r =>
        r.sourceId && r.sourceName ? (
          <Link to={`/reports/sources/${r.sourceId}`} style={pillLink}>{r.sourceName}</Link>
        ) : (
          <span style={{ color: '#d1d5db' }}>Unassigned</span>
        ),
    },
    {
      key: 'body',
      label: 'Data Body',
      width: '1fr',
      sortValue: r => r.dataBodyName ?? '',
      render: r => (
        <span style={{ fontWeight: 600, color: '#111827' }}>
          {r.dataBodyName || <span style={{ color: '#9ca3af' }}>(deleted body)</span>}
        </span>
      ),
    },
    { key: 'today', label: 'Today', width: '100px', align: 'right', sortValue: r => r.today, render: r => fmtExact(r.today) },
    { key: 'range', label: rangeLabel, width: '110px', align: 'right', sortValue: r => r.range, render: r => fmtExact(r.range) },
    { key: 'gross', label: 'Gross', width: '120px', align: 'right', sortValue: r => r.gross, render: r => fmtExact(r.gross) },
    {
      key: 'rate',
      label: 'Delivery Rate',
      width: '130px',
      align: 'right',
      title: 'Leads delivered ÷ leads accepted, over the selected range. Fan-out aware — over 100% is normal.',
      sortValue: r => r.deliveryRate,
      render: r => (
        <span style={{ fontWeight: 600, color: deliveryRateColor(r.deliveryRate) }}>
          {fmtRate(r.deliveryRate)}
        </span>
      ),
    },
    {
      key: 'drill',
      label: '',
      width: '80px',
      align: 'right',
      render: r => <Link to={`/reports/bodies/${r.dataBodyId}`} style={drillLink}>Drill →</Link>,
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={rows}
      rowKey={r => r.dataBodyId}
      defaultSort="range"
      emptyMessage="No data body activity in this range."
    />
  );
}

// ---------------------------------------------------------------------
// Tab 2 — By leg
// ---------------------------------------------------------------------

function LegsTable({
  rows,
  rangeLabel,
}: {
  rows: api.ReportingLegVolumeRow[];
  rangeLabel: string;
}) {
  const columns: Column<api.ReportingLegVolumeRow>[] = [
    {
      key: 'client',
      label: 'Client',
      width: '150px',
      sortValue: r => r.clientName ?? '',
      render: r =>
        r.clientId !== api.UNASSIGNED_CLIENT ? (
          <Link to={`/reports/clients/${r.clientId}`} style={pillLink}>
            {r.clientName || '(unnamed client)'}
          </Link>
        ) : (
          <span style={{ color: '#d1d5db' }}>Unassigned</span>
        ),
    },
    {
      key: 'leg',
      label: 'Leg',
      width: '1fr',
      sortValue: r => r.nodeName ?? '',
      render: r => (
        <span style={{ fontWeight: 600, color: '#111827' }}>
          {r.nodeName || r.nodeType || '(unnamed destination)'}
          {r.detached && (
            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#9ca3af' }}>
              deleted
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'body',
      label: 'Data Body',
      width: '160px',
      sortValue: r => r.dataBodyName ?? '',
      render: r =>
        r.dataBodyId && r.dataBodyName ? (
          <Link to={`/reports/bodies/${r.dataBodyId}`} style={{ ...pillLink, color: '#6b7280' }}>
            {r.dataBodyName}
          </Link>
        ) : (
          <span style={{ color: '#d1d5db' }}>—</span>
        ),
    },
    { key: 'today', label: 'Today', width: '100px', align: 'right', sortValue: r => r.today, render: r => fmtExact(r.today) },
    { key: 'range', label: rangeLabel, width: '110px', align: 'right', sortValue: r => r.range, render: r => fmtExact(r.range) },
    {
      key: 'gross',
      label: 'Gross',
      width: '120px',
      align: 'right',
      title:
        'Lifetime deliveries, from this destination\'s durable counter. ' +
        'Destinations created before 2026-06-03 predate leg-node reporting, ' +
        'so the counter is the only complete record of their early volume.',
      sortValue: r => r.gross,
      render: r => fmtExact(r.gross),
    },
    {
      key: 'rate',
      label: 'Accept Rate',
      width: '120px',
      align: 'right',
      title: 'Delivered ÷ (delivered + failed) for this destination, over the selected range.',
      sortValue: r => r.acceptRate,
      render: r => (
        <span style={{ fontWeight: 600, color: acceptRateColor(r.acceptRate) }}>
          {fmtRate(r.acceptRate)}
        </span>
      ),
    },
    {
      key: 'drill',
      label: '',
      width: '80px',
      align: 'right',
      render: r => <Link to={`/reports/legs/${r.legNodeId}`} style={drillLink}>Drill →</Link>,
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={rows}
      rowKey={r => r.legNodeId}
      defaultSort="range"
      emptyMessage="No delivery activity in this range."
    />
  );
}

// ---------------------------------------------------------------------
// Tab 3 — By intake type
// ---------------------------------------------------------------------

/**
 * The ingest-CHANNEL split (CSV / Webhook / API). Fed `overview.sources`
 * because that is still the API field name; the UI calls it "intake type"
 * so the word "source" means one thing only — the company sending the
 * leads. See migration 030.
 */
function TypeTable({ types }: { types: api.ReportingSourceRow[] }) {
  const labelFor = (s: string) => ({
    csv: 'CSV Upload',
    webhook: 'Webhook',
    api: 'API',
    unknown: 'Unknown (pre-rollup data)',
  } as Record<string, string>)[s] || s;

  const columns: Column<api.ReportingSourceRow>[] = [
    {
      key: 'type',
      label: 'Intake Type',
      width: '1fr',
      sortValue: r => labelFor(r.source),
      render: r => <span style={{ fontWeight: 600, color: '#111827' }}>{labelFor(r.source)}</span>,
    },
    {
      key: 'accepts', label: 'Accepted', width: '130px', align: 'right',
      sortValue: r => r.accepts,
      render: r => <span style={{ color: '#1d4ed8' }}>{fmtExact(r.accepts)}</span>,
    },
    {
      key: 'rejects', label: 'Rejected', width: '130px', align: 'right',
      sortValue: r => r.rejects,
      render: r => <span style={{ color: '#b91c1c' }}>{fmtExact(r.rejects)}</span>,
    },
    {
      key: 'rate', label: 'Accept Rate', width: '130px', align: 'right',
      sortValue: r => (r.accepts + r.rejects === 0 ? null : r.accepts / (r.accepts + r.rejects)),
      render: r => {
        const total = r.accepts + r.rejects;
        const rate = total === 0 ? null : r.accepts / total;
        return (
          <span style={{ fontWeight: 600, color: acceptRateColor(rate) }}>{fmtRate(rate)}</span>
        );
      },
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={types}
      rowKey={r => r.source}
      defaultSort="accepts"
      emptyMessage="No brain traffic in this range."
    />
  );
}

// ---------------------------------------------------------------------
// Tab 4 — By source
// ---------------------------------------------------------------------

/**
 * One row per SOURCE — the company sending leads in — rolled up across
 * every data body it owns. Kept as its own tab (rather than folded into
 * the Source dropdown) because comparing partners side by side is the
 * whole point; a picker can only show one at a time.
 *
 * Note this table is NOT scoped by the Source filter — it is the picker's
 * companion view and must keep showing every partner.
 */
function SourceCompanyTable({ rows }: { rows: api.ReportingSourceCompanyRow[] }) {
  const columns: Column<api.ReportingSourceCompanyRow>[] = [
    {
      key: 'source', label: 'Source', width: '1fr',
      sortValue: r => (r.sourceId === api.UNASSIGNED_SOURCE ? '' : r.sourceName ?? ''),
      render: r => (
        <span style={{
          fontWeight: 600,
          color: r.sourceId === api.UNASSIGNED_SOURCE ? '#9ca3af' : '#111827',
        }}>
          {r.sourceId === api.UNASSIGNED_SOURCE ? 'Unassigned' : r.sourceName || '(unnamed source)'}
        </span>
      ),
    },
    {
      key: 'bodies', label: 'Bodies', width: '100px', align: 'right',
      sortValue: r => r.bodyCount,
      render: r => <span style={{ color: '#6b7280' }}>{r.bodyCount}</span>,
    },
    {
      key: 'accepts', label: 'Accepted', width: '130px', align: 'right',
      sortValue: r => r.accepts,
      render: r => <span style={{ color: '#1d4ed8' }}>{fmtExact(r.accepts)}</span>,
    },
    {
      key: 'rejects', label: 'Rejected', width: '130px', align: 'right',
      sortValue: r => r.rejects,
      render: r => <span style={{ color: '#b91c1c' }}>{fmtExact(r.rejects)}</span>,
    },
    {
      key: 'rate', label: 'Accept Rate', width: '130px', align: 'right',
      sortValue: r => r.acceptRate,
      render: r => (
        <span style={{ fontWeight: 600, color: acceptRateColor(r.acceptRate) }}>
          {fmtRate(r.acceptRate)}
        </span>
      ),
    },
    {
      key: 'drill', label: '', width: '80px', align: 'right',
      render: r => <Link to={`/reports/sources/${r.sourceId}`} style={drillLink}>Drill →</Link>,
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={rows}
      rowKey={r => r.sourceId}
      defaultSort="accepts"
      emptyMessage="No source activity in this range."
    />
  );
}

// ---------------------------------------------------------------------
// Tab 5 — By client
// ---------------------------------------------------------------------

/**
 * One row per CLIENT — who we deliver TO — rolled up across every delivery
 * destination assigned to them. The outbound counterpart of By source.
 *
 * "Delivered / Failed" rather than "Accepts / Rejects", because on the
 * outbound side an accept IS a delivered lead. The destination count
 * includes destinations whose node has since been deleted: their history
 * still belongs to the client (migration 031), so hiding them would make
 * the row's own totals not add up.
 */
function ClientTable({ rows }: { rows: api.ReportingClientRow[] }) {
  const columns: Column<api.ReportingClientRow>[] = [
    {
      key: 'client', label: 'Client', width: '1fr',
      sortValue: r => (r.clientId === api.UNASSIGNED_CLIENT ? '' : r.clientName ?? ''),
      render: r => (
        <span style={{
          fontWeight: 600,
          color: r.clientId === api.UNASSIGNED_CLIENT ? '#9ca3af' : '#111827',
        }}>
          {r.clientId === api.UNASSIGNED_CLIENT ? 'Unassigned' : r.clientName || '(unnamed client)'}
        </span>
      ),
    },
    {
      key: 'nodes', label: 'Destinations', width: '120px', align: 'right',
      sortValue: r => r.nodeCount,
      render: r => <span style={{ color: '#6b7280' }}>{r.nodeCount}</span>,
    },
    {
      key: 'bodies', label: 'Bodies', width: '100px', align: 'right',
      sortValue: r => r.bodyCount,
      render: r => <span style={{ color: '#6b7280' }}>{r.bodyCount}</span>,
    },
    {
      key: 'delivered', label: 'Delivered', width: '130px', align: 'right',
      sortValue: r => r.delivered,
      render: r => <span style={{ color: '#1d4ed8' }}>{fmtExact(r.delivered)}</span>,
    },
    {
      key: 'failed', label: 'Failed', width: '110px', align: 'right',
      sortValue: r => r.failed,
      render: r => <span style={{ color: '#b91c1c' }}>{fmtExact(r.failed)}</span>,
    },
    {
      key: 'rate', label: 'Delivery Rate', width: '130px', align: 'right',
      title: 'Delivered ÷ attempted across this client\'s destinations.',
      sortValue: r => r.deliveryRate,
      render: r => (
        <span style={{ fontWeight: 600, color: acceptRateColor(r.deliveryRate) }}>
          {fmtRate(r.deliveryRate)}
        </span>
      ),
    },
    {
      key: 'drill', label: '', width: '80px', align: 'right',
      render: r => <Link to={`/reports/clients/${r.clientId}`} style={drillLink}>Drill →</Link>,
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={rows}
      rowKey={r => r.clientId}
      defaultSort="delivered"
      emptyMessage="No delivery activity in this range."
    />
  );
}

const drillLink: React.CSSProperties = {
  fontSize: 13,
  color: '#2563eb',
  fontWeight: 600,
  textDecoration: 'none',
};

const pillLink: React.CSSProperties = {
  fontSize: 13,
  color: '#2563eb',
  fontWeight: 600,
  textDecoration: 'none',
};
